import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { UserX, MousePointer } from "lucide-react";
import { toast } from "sonner";

// Inactivity threshold: 30 minutes
const INACTIVITY_THRESHOLD_MS = 30 * 60 * 1000;
// Warning shown 2 minutes before auto-logout
const WARNING_BEFORE_LOGOUT_MS = 2 * 60 * 1000;
// Check inactivity every 30 seconds
const CHECK_INTERVAL_MS = 30 * 1000;

// Events that indicate user activity
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

// localStorage key for cross-tab activity sync
const ACTIVITY_STORAGE_KEY = "checkmaty_last_activity";

// Helper to safely read from localStorage
const getStoredActivity = (): number => {
  try {
    const stored = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
  } catch {
    // localStorage might be unavailable
  }
  return Date.now();
};

// Helper to safely write to localStorage
const setStoredActivity = (timestamp: number): void => {
  try {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, timestamp.toString());
  } catch {
    // localStorage might be unavailable (private mode, quota exceeded)
  }
};

export function InactivityWarning() {
  const { t } = useTranslation();
  const { session, signOut, loading } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const lastActivityRef = useRef<number>(getStoredActivity());
  const warningShownRef = useRef(false);
  // Track if session has been successfully loaded at least once
  const sessionLoadedRef = useRef(false);
  // Track session ID to detect session changes
  const lastSessionIdRef = useRef<string | null>(null);

  // Guard: Only enable inactivity tracking after session is fully loaded
  // This prevents logout loops when network issues occur during initial load
  const isSessionReady = !loading && session !== null;

  // Reset activity when session changes (new login or refresh) to prevent immediate logout
  // from stale localStorage data - using useEffect to avoid render-time side effects
  useEffect(() => {
    if (session?.access_token && session.access_token !== lastSessionIdRef.current) {
      lastSessionIdRef.current = session.access_token;
      // Fresh/refreshed session - reset activity timestamp
      const now = Date.now();
      lastActivityRef.current = now;
      setStoredActivity(now);
      sessionLoadedRef.current = true;
      // Also dismiss any warning that might be showing
      setShowWarning(false);
      warningShownRef.current = false;
    }
  }, [session?.access_token]);

  // Reset activity timestamp
  const resetActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    // Sync to other tabs via localStorage
    setStoredActivity(now);
    // If warning is shown and user interacts, dismiss it
    if (showWarning) {
      setShowWarning(false);
      warningShownRef.current = false;
      toast.success(t("auth.activityDetected"));
    }
  }, [showWarning, t]);

  // Handle continue button click
  const handleContinue = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    // Sync to other tabs via localStorage
    setStoredActivity(now);
    setShowWarning(false);
    warningShownRef.current = false;
    toast.success(t("auth.sessionContinued"));
  }, [t]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    setShowWarning(false);
    if (signOut) {
      await signOut();
    }
  }, [signOut]);

  // Set up activity listeners - only when session is ready
  useEffect(() => {
    if (!isSessionReady) return;

    const handleActivity = () => {
      // Only reset if warning is not shown (to prevent accidental dismissal)
      if (!warningShownRef.current) {
        const now = Date.now();
        lastActivityRef.current = now;
        // Sync to other tabs
        setStoredActivity(now);
      }
    };

    // Add event listeners
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isSessionReady]);

  // Listen for activity from other tabs - only when session is ready
  useEffect(() => {
    if (!isSessionReady) return;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === ACTIVITY_STORAGE_KEY && event.newValue) {
        const otherTabActivity = parseInt(event.newValue, 10);
        // Only update if the other tab's activity is more recent
        if (!isNaN(otherTabActivity) && otherTabActivity > lastActivityRef.current) {
          lastActivityRef.current = otherTabActivity;
          // Dismiss warning if it was shown
          if (warningShownRef.current) {
            setShowWarning(false);
            warningShownRef.current = false;
            // Don't show toast for cross-tab activity to avoid confusion
          }
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isSessionReady]);

  // Check for inactivity periodically - only when session is fully ready
  useEffect(() => {
    // Guard: Don't start inactivity checks until session is fully loaded
    // This prevents logout loops when auth is still loading or network is slow
    if (!isSessionReady || !sessionLoadedRef.current) {
      setShowWarning(false);
      return;
    }

    const checkInactivity = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      const timeUntilLogout = INACTIVITY_THRESHOLD_MS - timeSinceActivity;

      // Time to auto-logout
      if (timeUntilLogout <= 0) {
        setShowWarning(false);
        toast.error(t("auth.inactivityLogout"), {
          description: t("auth.inactivityLogoutDescription"),
        });
        if (signOut) signOut();
        return;
      }

      // Show warning when approaching logout
      if (timeUntilLogout <= WARNING_BEFORE_LOGOUT_MS) {
        setTimeRemaining(timeUntilLogout);
        if (!warningShownRef.current) {
          setShowWarning(true);
          warningShownRef.current = true;
        }
      } else {
        setShowWarning(false);
        warningShownRef.current = false;
        setTimeRemaining(null);
      }
    };

    // Initial check
    checkInactivity();

    // Periodic check
    const interval = setInterval(checkInactivity, CHECK_INTERVAL_MS);
    // Unref the timer in Node.js to prevent blocking process exit (CI/Vitest)
    if (
      typeof (interval as ReturnType<typeof setInterval> & { unref?: () => void })?.unref ===
      "function"
    ) {
      (interval as ReturnType<typeof setInterval> & { unref?: () => void }).unref!();
    }

    return () => clearInterval(interval);
  }, [isSessionReady, signOut, t]);

  // Update countdown every second when warning is shown
  useEffect(() => {
    if (!showWarning) return;

    const countdownInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;
      const remaining = INACTIVITY_THRESHOLD_MS - timeSinceActivity;

      if (remaining > 0) {
        setTimeRemaining(remaining);
      } else {
        // Auto-logout
        setShowWarning(false);
        toast.error(t("auth.inactivityLogout"), {
          description: t("auth.inactivityLogoutDescription"),
        });
        if (signOut) signOut();
      }
    }, 1000);
    // Unref the timer in Node.js to prevent blocking process exit (CI/Vitest)
    if (
      typeof (countdownInterval as ReturnType<typeof setInterval> & { unref?: () => void })
        ?.unref === "function"
    ) {
      (countdownInterval as ReturnType<typeof setInterval> & { unref?: () => void }).unref!();
    }

    return () => clearInterval(countdownInterval);
  }, [showWarning, signOut, t]);

  const formatTimeRemaining = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  if (!showWarning) return null;

  return (
    <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-destructive" />
            {t("auth.inactivityWarning")}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>{t("auth.inactivityWarningDescription")}</p>
            {timeRemaining !== null && (
              <p className="text-lg font-semibold text-destructive">
                {t("auth.autoLogoutIn")}: {formatTimeRemaining(timeRemaining)}
              </p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={handleLogout} className="w-full sm:w-auto">
            {t("auth.logout")}
          </Button>
          <AlertDialogAction asChild>
            <Button onClick={handleContinue} className="w-full sm:w-auto">
              <MousePointer className="mr-2 h-4 w-4" />
              {t("auth.continueSession")}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
